import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setTokenGetter } from './api';
import { resetTerms } from './terms';

export type Role = 'parent' | 'child' | 'teen' | 'grandparent' | 'tutor' | 'safeguarding_admin' | 'content_editor';

export interface SessionUser {
  id: string;
  role: Role;
  displayName?: string;
  email?: string | null;
  familyId: string | null;
  childProfileId: string | null;
}

interface ChildMode {
  token: string;
  childName: string;
  parentToken: string;
  parentUser: SessionUser;
}

interface AuthState {
  token: string | null;
  user: SessionUser | null;
  childMode: ChildMode | null;
  signIn(token: string, user: SessionUser): void;
  signOut(): void;
  enterChildMode(token: string, childId: string, childName: string): void;
  exitChildMode(password: string): Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);
// sessionStorage: tokens vanish when the browser tab closes (shared family devices).
const KEY = 'iran.session.v1';

interface Stored {
  token: string;
  user: SessionUser;
  childMode: ChildMode | null;
}

function load(): Stored | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Stored | null>(load);

  useEffect(() => {
    try {
      if (state) sessionStorage.setItem(KEY, JSON.stringify(state));
      else sessionStorage.removeItem(KEY);
    } catch {
      /* storage unavailable */
    }
  }, [state]);

  setTokenGetter(() => state?.token ?? null);

  const signIn = useCallback((token: string, user: SessionUser) => setState({ token, user, childMode: null }), []);
  const signOut = useCallback(() => {
    resetTerms();
    setState(null);
  }, []);

  const enterChildMode = useCallback((token: string, childId: string, childName: string) => {
    setState((s) =>
      s
        ? {
            token,
            user: { id: s.user.id, role: 'child', familyId: s.user.familyId, childProfileId: childId, displayName: childName },
            childMode: { token, childName, parentToken: s.token, parentUser: s.user },
          }
        : s,
    );
  }, []);

  /** Leaving child mode needs the parent's password, so children cannot wander into parent settings. */
  const exitChildMode = useCallback(
    async (password: string) => {
      const cm = state?.childMode;
      if (!cm) return;
      const r = await api<{ token: string; user: SessionUser }>('/auth/login', { body: { email: cm.parentUser.email, password }, token: null });
      setState({ token: r.token, user: r.user, childMode: null });
    },
    [state],
  );

  const value = useMemo<AuthState>(
    () => ({ token: state?.token ?? null, user: state?.user ?? null, childMode: state?.childMode ?? null, signIn, signOut, enterChildMode, exitChildMode }),
    [state, signIn, signOut, enterChildMode, exitChildMode],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
